import { container } from 'tsyringe'
import {
  AnimationAction,
  AnimationClip,
  AnimationMixer,
  Color,
  DoubleSide,
  LinearEncoding,
  LoopOnce,
  Mesh,
  MeshBasicMaterial,
  MeshStandardMaterial,
  Object3D,
  PlaneGeometry,
  RawShaderMaterial,
  SkinnedMesh,
  Texture,
  Vector2
} from 'three'
import { PhysicsLayerZ } from '../misc/constants'
import CanvasText from './../misc/canvas-text'
import gsap, { Sine } from 'gsap'
import { Interface } from '@/shared/di/interface'
import type { IAssets } from '@/shared/di/interface/assets'
import { WalletType } from './wallet-manager'
import { clone } from 'three/examples/jsm/utils/SkeletonUtils'
import { IWalletFaceOutside, WalletFaceInterpreter } from '@/shared/di/interface/walletfaces'

const enum AnimationActions {
  OPEN = 'Open_Anim',
  COIN_ADD = 'CoinAdd_Anim',
  CLOSE = 'Close_Anim'
}

export default class Wallet {
  type: WalletType
  model: Object3D | undefined
  furMesh: Mesh
  furMeshBack: Mesh
  //closeButtonMesh: Mesh | undefined
  canvasText: CanvasText
  name = ''
  available = false
  emitParticles = false
  animatedPulse = 0
  incomingAssets = false

  private isAnimating = false
  private pulseTween: gsap.core.Tween | undefined
  private walletMesh: SkinnedMesh | undefined
  private walletMaterial: MeshStandardMaterial | undefined
  private labelMesh: Mesh
  private tubeNeutralY = 0
  private dragNearExtraY = 0
  private tubePosition = new Vector2()
  private animationMixer: AnimationMixer | undefined
  private animationActions: {
    [key: string]: AnimationAction
  } = {}
  private nearInterpolation = 0
  private glow = 0

  constructor(type: WalletType, tubePosition: Vector2) {
    this.type = type
    this.tubePosition.copy(tubePosition)

    this.canvasText = new CanvasText()
    const tex = this.canvasText.getTexture()
    tex.repeat.y = 0.9
    this.labelMesh = new Mesh(
      new PlaneGeometry(5.12 * 1.2, 1.28 * 1.2, 1, 1),
      new MeshBasicMaterial({
        map: tex,
        transparent: true,
        depthTest: false,
        depthWrite: false,
        side: DoubleSide
      })
    )

    this.labelMesh.userData.enableBloom = false
    this.labelMesh.position.y = tubePosition.y + 0.8
    this.labelMesh.position.z = 0.45 + PhysicsLayerZ.Front
    this.labelMesh.position.x = 0

    const maskMaterial = new RawShaderMaterial({
      transparent: true,
      uniforms: {
        tMap: { value: new Texture() },
        tMask: { value: new Texture() }
      },
      vertexShader: `
      precision highp float;
      precision mediump int;

      uniform mat4 modelViewMatrix; // optional
      uniform mat4 projectionMatrix; // optional

      attribute vec3 position;
      attribute vec2 uv;
      varying vec2 vUv;

      void main()	{
        vUv = vec2(uv.x,1.0 - uv.y);
        gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );
      }
      `,
      fragmentShader: `
      precision mediump float;
      precision mediump int;

      varying vec2 vUv;

      uniform sampler2D tMap;
      uniform sampler2D tMask;

      void main() {
          vec2 uv = vUv;
          vec4 color = texture2D( tMap, vUv );
          vec4 mask = texture2D( tMask, vUv );

          gl_FragColor = color;
          gl_FragColor.a = mask.g * (1.0-smoothstep(0.95,1.0,vUv.y));
      }
      `
    })

    this.furMesh = new Mesh(new PlaneGeometry(7.5, 7.5, 1, 1), maskMaterial)
    this.furMesh.userData.hideFromBloom = true
    this.furMesh.userData.enableBloom = false
    this.furMesh.position.y = tubePosition.y + 3
    this.furMesh.position.z = 0.3 + PhysicsLayerZ.Front
    this.furMesh.position.x = 0
    this.furMesh.visible = false

    this.furMeshBack = new Mesh(new PlaneGeometry(7.5, 7.5, 1, 1), maskMaterial)
    this.furMeshBack.userData.hideFromBloom = true
    this.furMeshBack.userData.enableBloom = false
    this.furMeshBack.position.y = tubePosition.y + 3.1
    this.furMeshBack.position.z = -0.5 + PhysicsLayerZ.Front
    this.furMeshBack.scale.x = -1
    this.furMeshBack.position.x = 0
    this.furMeshBack.visible = false

    this.init()
  }

  setConfig(config: IWalletFaceOutside) {
    //possible settings options
    let settingsStore = {
      diffuseMapKey: undefined,
      diffuseColor: undefined,
      normalMapKey: undefined,
      metalness: undefined,
      roughness: undefined,
      roughnessMapKey: undefined,
      metalnessMapKey: undefined,
      walletLightKey: undefined,
      furMapKey: undefined,
      nameColor: undefined
    }

    //get segttings from json
    const settings = config.behaviours.find(b => b.interpreter === WalletFaceInterpreter.Settings)

    if (settings && settings.data) {
      settingsStore = Object.assign(settingsStore, JSON.parse(settings.data))
    }

    if (settingsStore.nameColor) {
      this.canvasText.setTextColor(settingsStore.nameColor)
    }

    const assets = container.resolve<IAssets>(Interface.IAssets)
    if (settingsStore.diffuseColor) {
      this.walletMaterial!.userData.shader.uniforms.walletColor.value = new Color(settingsStore.diffuseColor)
      this.walletMaterial!.color.set(settingsStore.diffuseColor)
      this.walletMaterial!.map = null
    } else {
      this.walletMaterial!.userData.shader.uniforms.walletColor.value.set(0xffffff)
      this.walletMaterial!.color.set(0xffffff)
    }

    if (Object.hasOwn(config, 'metalness')) {
      this.walletMaterial!.metalness = settingsStore.metalness ?? 0
    }

    if (Object.hasOwn(config, 'roughness')) {
      this.walletMaterial!.roughness = settingsStore.roughness ?? 0
    }

    //if (this.type === WalletType.Tjing) {
    this.walletMaterial!.envMapIntensity = 0.8
    //}

    if (settingsStore.normalMapKey) {
      const asset = assets.getTexture(settingsStore.normalMapKey)
      if (asset) {
        this.walletMaterial!.normalMap = asset
      }
    } else {
      this.walletMaterial!.normalMap = null
    }

    if (settingsStore.diffuseMapKey) {
      const asset = assets.getTexture(settingsStore.diffuseMapKey)
      if (asset) {
        this.walletMaterial!.map = asset

        if (settingsStore.diffuseMapKey == 'wallet_lightmap') {
          asset.encoding = LinearEncoding
        }
      }
    } else {
      this.walletMaterial!.map = null
    }

    if (settingsStore.roughnessMapKey) {
      const asset = assets.getTexture(settingsStore.roughnessMapKey)
      if (asset) {
        asset.encoding = LinearEncoding
        this.walletMaterial!.roughnessMap = asset
      }
    } else {
      this.walletMaterial!.roughnessMap = null
    }

    if (settingsStore.metalnessMapKey) {
      const asset = assets.getTexture(settingsStore.metalnessMapKey)
      if (asset) {
        asset.encoding = LinearEncoding
        this.walletMaterial!.metalnessMap = asset
        this.walletMaterial!.metalness = 1
      }
    } else {
      this.walletMaterial!.metalnessMap = null
      this.walletMaterial!.metalness = 0
    }

    if (settingsStore.walletLightKey) {
      const asset = assets.getTexture(settingsStore.walletLightKey)

      if (asset) {
        asset.encoding = LinearEncoding
        this.walletMaterial!.userData.shader.uniforms.walletLight.value = asset
      }
    }

    if (settingsStore.furMapKey) {
      const furMaterial = this.furMesh!.material as RawShaderMaterial
      const asset = assets.getTexture(settingsStore.furMapKey)
      if (asset) {
        furMaterial.uniforms.tMap.value = asset
        furMaterial.uniforms.tMask.value = assets.getTexture('fur_mask')

        this.furMesh.visible = true
        this.furMeshBack.visible = true
      }
    } else {
      this.furMesh.visible = false
      this.furMeshBack.visible = false
    }

    this.walletMaterial!.needsUpdate = true
  }

  setName(name: string) {
    if (name !== '') {
      this.canvasText.setRightName(name)
    } /*else {
      console.warn('no name')
    }*/
  }

  setAmount(amount: string) {
    if (amount !== '') {
      this.canvasText.setRightText(amount)
    }
  }

  init() {
    const assets = container.resolve<IAssets>(Interface.IAssets)

    // add tube
    const walletRoot = assets.getModel('wallet')

    if (!walletRoot) {
      return
    }

    const animations = assets.getAnimations('wallet')

    this.model = clone(walletRoot)
    this.model.rotation.y = Math.PI
    this.model.position.y = this.tubePosition.y
    this.model.position.z = PhysicsLayerZ.Front

    this.model.scale.multiplyScalar(82)

    this.walletMesh = this.model.children[1] as SkinnedMesh

    this.walletMesh.skeleton.bones.find(bone => bone.name === 'Bone_Front')!.attach(this.labelMesh)
    this.walletMesh!.skeleton.bones.find(bone => bone.name === 'Bone_Front')!.attach(this.furMesh)
    this.walletMesh!.skeleton.bones.find(bone => bone.name === 'Bone_Back')!.attach(this.furMeshBack)
    //this.labelMesh.userData.hideFromBloom = true

    this.walletMesh.userData.enableBloom = true
    this.createWalletMaterial()

    this.walletMesh.material = this.walletMaterial!

    this.animationMixer = new AnimationMixer(this.walletMesh)
    if (animations) {
      animations.forEach((clip: AnimationClip) => {
        const action = this.animationMixer!.clipAction(clip)
        const name = clip.name
        action.loop = LoopOnce
        action.clampWhenFinished = true

        this.animationActions[name] = action
      })

      this.animationActions[AnimationActions.COIN_ADD].play()
      this.animationActions[AnimationActions.CLOSE].timeScale = 1.5
    }
  }

  createWalletMaterial() {
    const material = new MeshStandardMaterial({
      vertexColors: false,
      map: new Texture()
    })

    material.onBeforeCompile = shader => {
      shader.uniforms.walletColor = { value: new Color(0xff00ff) }
      shader.uniforms.isBloomRender = { value: 0 }
      shader.uniforms.glow = { value: this.glow }
      shader.uniforms.extraGlow = { value: 0 }
      shader.uniforms.time = { value: 0 }

      shader.uniforms.walletLight = { value: null }
      if (material.userData.shader) {
        shader.uniforms.walletColor.value = material.userData.shader.uniforms.walletColor.value
        shader.uniforms.walletLight.value = material.userData.shader.uniforms.walletLight.value
        shader.uniforms.extraGlow.value = material.userData.shader.uniforms.extraGlow.value
        shader.uniforms.time.value = material.userData.shader.uniforms.time.value
      }

      shader.fragmentShader = shader.fragmentShader
        .replace(
          'void main() {',
          `
          uniform float glow; 
          uniform vec3 walletColor; 
          uniform float extraGlow; 
          uniform float time; 
          uniform float isBloomRender; 
          uniform sampler2D walletLight; 

          vec3 blendOverlay(vec3 base, vec3 blend) {
            return mix(1.0 - 2.0 * (1.0 - base) * (1.0 - blend), 2.0 * base * blend, step(base, vec3(0.5)));
          }

          vec3 blendHardLight(vec3 base, vec3 blend) {
            return blendOverlay(blend,base);
          }
          
          vec3 blendHardLight(vec3 base, vec3 blend, float opacity) {
            return (blendHardLight(base, blend) * opacity + base * (1.0 - opacity));
          }

          void main() {
          `
        )

        .replace('#include <color_fragment>', '')
        .replace(
          '#include <output_fragment>',
          `
          vec4 color = vec4(blendHardLight( walletColor, outgoingLight.rgb),1.0);

          vec4 lightColor = texture2D(walletLight,vUv);
          
          float glowTotal = 0.0;

          glowTotal += extraGlow;
          glowTotal += glow;

          color.rgb += lightColor.rgb * glowTotal;
          
          gl_FragColor = mix(color, vec4(lightColor.rgb,1.0)*glowTotal, isBloomRender);
          //gl_FragColor.rgb = mix(walletColor, vec3(0.0), isBloomRender);
          
        `
        )

      material.userData.shader = shader
    }

    this.walletMaterial = material
  }

  open() {
    if (this.animationActions[AnimationActions.OPEN].time < 0.5) {
      this.animationActions[AnimationActions.CLOSE].weight = 0
      this.animationActions[AnimationActions.COIN_ADD].weight = 0
      this.animationActions[AnimationActions.OPEN].weight = 1

      this.animationActions[AnimationActions.CLOSE].stop()
      this.animationActions[AnimationActions.OPEN].reset()
      this.animationActions[AnimationActions.OPEN].play()
    }
  }

  close() {
    if (this.animationActions[AnimationActions.CLOSE].time < 0.5) {
      this.animationActions[AnimationActions.OPEN].stop()
      this.animationActions[AnimationActions.CLOSE].reset()
      this.animationActions[AnimationActions.CLOSE].play()

      this.animationActions[AnimationActions.COIN_ADD].weight = 0
      this.animationActions[AnimationActions.OPEN].weight = 0
      this.animationActions[AnimationActions.CLOSE].weight = 1
    }
  }

  reset() {
    this.animationActions[AnimationActions.OPEN].stop()
    this.animationActions[AnimationActions.CLOSE].stop()
    this.animationActions[AnimationActions.COIN_ADD].stop()
    this.animationActions[AnimationActions.COIN_ADD].weight = 0
    this.animationActions[AnimationActions.OPEN].weight = 0
    this.animationActions[AnimationActions.CLOSE].weight = 1
  }

  update(time: number, delta: number) {
    if (this.walletMaterial) {
      //this.walletMaterial.userData.shader.uniforms.time.value = time
    }

    if (this.animationMixer) {
      const action = this.animationActions[AnimationActions.COIN_ADD]

      this.animationActions[AnimationActions.COIN_ADD].weight = this.nearInterpolation
      this.animationActions[AnimationActions.COIN_ADD].time = this.nearInterpolation * action.getClip().duration

      if (this.animationActions[AnimationActions.COIN_ADD].time > 0.01) {
        this.animationActions[AnimationActions.COIN_ADD].weight = 1
        this.animationActions[AnimationActions.OPEN].weight = 0
        this.animationActions[AnimationActions.CLOSE].weight = 0
      } else {
        this.animationActions[AnimationActions.COIN_ADD].weight = 0
        this.animationActions[AnimationActions.OPEN].weight = 1
        this.animationActions[AnimationActions.CLOSE].weight = 1
      }
      this.animationMixer.update(delta)

      //this.model!.rotation.x = this.nearInterpolation * -0.15
    }
  }

  setOnline(status: boolean) {}

  setAvailable(value: boolean) {
    this.available = value
    this.updateGlow()
  }

  setIncomingAssets(newStatus: boolean) {
    if (!newStatus && this.incomingAssets) {
      this.animateCoinOut()
    }

    this.incomingAssets = newStatus

    if (!this.incomingAssets) {
      this.killTween()
      this.updateGlow()
    } else {
      if (!this.pulseTween) {
        this.pulseTween = gsap.to(this, {
          duration: 0.7,
          animatedPulse: 1,
          repeat: -1,
          yoyo: true,
          ease: Sine.easeIn,
          onUpdate: () => {
            this.updateGlow()
          }
        })
      }
    }
  }

  killTween() {
    if (this.pulseTween) {
      this.pulseTween.kill()
      this.pulseTween = undefined
      this.animatedPulse = 0
    }
  }

  animateCoinOut() {
    if (!this.model) {
      return
    }
    gsap.to(this.model.position, {
      y: this.tubePosition.y - 1,
      duration: 0.2,
      ease: Sine.easeInOut,
      onComplete: () => {
        gsap.to(this.model!.position, {
          y: this.tubePosition.y,
          duration: 1
        })
      }
    })
  }

  setCoinNearTube(distance: number) {
    if (!this.model) {
      return
    }

    if (distance > 0) {
      if (gsap.getTweensOf(this.model.position).length > 0) {
        gsap.killTweensOf(this.model.position)
        this.isAnimating = false
      }
    }

    if (!this.isAnimating && distance > 0) {
      this.dragNearExtraY = Math.min(0.2, distance)
      this.model.position.y = this.tubePosition.y - this.dragNearExtraY
      this.walletMaterial!.userData.shader.uniforms.extraGlow.value = this.dragNearExtraY / 0.5
      this.nearInterpolation = this.dragNearExtraY / 0.4
    } else {
      this.dragNearExtraY += (0 - this.dragNearExtraY) * 0.4
      this.model.position.y += (this.tubePosition.y - this.model.position.y) * 0.1
      this.walletMaterial!.userData.shader.uniforms.extraGlow.value +=
        (0 - this.walletMaterial!.userData.shader.uniforms.extraGlow.value) * 0.1

      this.nearInterpolation += (0 - this.nearInterpolation) * 0.3
    }
  }

  animateCoinIn() {
    if (!this.model) {
      return
    }

    this.isAnimating = true

    gsap.killTweensOf(this.model.position)

    gsap.to(this.model.position, {
      y: this.tubePosition.y + this.dragNearExtraY + 1,
      duration: 0.2,
      ease: Sine.easeInOut,
      onComplete: () => {
        this.dragNearExtraY = 0
        gsap.to(this.model!.position, {
          y: this.tubePosition.y,
          ease: Sine.easeOut,
          duration: 1,
          onComplete: () => {
            this.isAnimating = false
          }
        })
      }
    })
  }

  updateGlow() {
    if (this.walletMaterial) {
      let lit = this.available ? 1 : 0
      if (this.incomingAssets) {
        lit = 1 - this.animatedPulse
      }

      this.glow = lit
      this.walletMaterial.userData.shader.uniforms.glow.value = lit
    }
  }

  destroy() {
    if (this.walletMesh) {
      this.walletMesh.geometry.dispose()
    }
    if (this.canvasText) {
      this.canvasText.destroy()
    }
    if (this.model && this.model.parent) {
      gsap.killTweensOf(this.model.position)
      this.model.parent.remove(this.model)
      this.model = undefined
    }
  }
}
